import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import "../View/PromocodeDashboard.css";
import {
  useDeletePromoCode,
  useGetAllPromoCodes,
} from "../../../store/Apis/StripeApi";
import CircularProgress from "@mui/material/CircularProgress";
import {
  PromoAlertData,
  PromoCodeStatus,
  PromotionDropdownItems,
  PromotionType,
} from "../../../Helper";
import { useNavigate } from "react-router-dom";
import { PromoFilter } from "../Components/PromoFlter";
import useDebounce from "../../../features/main-page/components/get-conversation/useDebounce";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { PromoCodeAlert } from "../../Components/PromoCodeAlert";
import { EditPromoCodeIcon } from "../../../Custom-imeges/PromoCodesIcons/EditPromoCodeIcon";
import { DeletePromoCodeIcon } from "../../../Custom-imeges/PromoCodesIcons/DeletePromoCodeIcon";
import { formatDate } from "../../../store/one-query/useMethodHelper";
import { setSnackBarAlert } from "../../../store/one-query/chatsSlice";
import { useAppDispatch } from "../../../store/hooks";

interface PromoCodeDashboardProps {
  promoAlert: PromoAlertData;
  setPromoAlert: (data: any) => void;
}

export const PromoCodeDashboard = ({
  setPromoAlert,
  promoAlert,
}: PromoCodeDashboardProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [promoCodesData, setPromoCodesData] = useState([]);
  const { mutate: DeletePromoCodeRequest, data: deleteResponse } =
    useDeletePromoCode();
  const handleDropdownToggle = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const debouncedSearchTerm = useDebounce(searchValue, 1000);
  const [deleteCodeId, setDeleteCodeIde] = useState("");
  const {
    mutate: GetAllPromoCodesRequest,
    isPending,
    isSuccess,
    data: PromoCodesData,
  } = useGetAllPromoCodes();
  const [rows, setRows] = useState<any[]>([]);

  useEffect(() => {
    if (deleteResponse) {
      let isCouponDeleted = deleteResponse?.deleted;
      if (isCouponDeleted) {
        GetAllPromoCodesRequest({});
        dispatch(
          setSnackBarAlert({
            show: true,
            message: "Promo-code deleted successfully",
            alertType: "success",
          })
        );
      }
    }
  }, [deleteResponse]);

  useEffect(() => {
    GetAllPromoCodesRequest({ code: searchValue.toLowerCase() });
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (PromoCodesData) {
      setPromoCodesData(PromoCodesData);
    }
  }, [PromoCodesData]);

  useEffect(() => {
    setRows(
      promoCodesData?.map((promoCode: any) =>
        createData(
          promoCode.code,
          promoCode.promotionType,
          promoCode.status,
          promoCode.createdAT,
          promoCode.expireAt,
          promoCode.used,
          promoCode.createdBy,
          promoCode.promoCodeId
        )
      )
    );
  }, [isSuccess, promoCodesData]);

  function createData(
    code: string,
    PromotionType: string,
    Status: string,
    Created: string,
    Expireddate: string,
    Usage: string,
    CreatedBy: string,
    codeId: string
  ) {
    return {
      code,
      PromotionType,
      Status,
      Created,
      Expireddate,
      Usage,
      CreatedBy,
      codeId,
    };
  }

  const handleGoToCreatePromo = () => {
    navigate("create-coupon");
  };

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchValue(event.target.value);
  };

  const handleShowAlertDeletePromoCode = (codeId: string) => {
    setDeleteCodeIde(codeId);
    setPromoAlert((prevState: PromoAlertData) => ({
      ...prevState,
      show: true,
      headerTitle: "Delete promo code",
      description: "You will no longer see this promo code here",
      firstAction: "Cancel",
      secAction: "Delete",
      alertType: "delete",
    }));
  };

  const handleDeletePromoCode = () => {
    DeletePromoCodeRequest(deleteCodeId);
  };

  const handleUpdatePromoCode = () => {
    navigate("update-coupon");
  };

  const handleViewPromoCode = (codeId: string) => {
    navigate(`view-coupon/${codeId}`);
  };

  return (
    <Box className="promo-dashboard">
      <Box className="promoCode-body-header">
        <Box className="heder-Content">
          <Typography className="page-header-main-title">
            Promo codes
          </Typography>
        </Box>
      </Box>
      <Box className="promo-dashboard-container">
        <Box className="actions-container">
          <Box className="promo-filtration">
            <Box className="promo-filtration-search">
              <TextField
                value={searchValue}
                onChange={handleSearchInputChange}
                placeholder="Search for promo code"
                id="input-with-icon-textfield"
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{
                          color: "var(--Main_icons_color)",
                          opacity: "0.5",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: "100%",
                  backgroundColor: "transparent",
                  border: "none",
                  boxShadow: "none",
                  "& .MuiInputBase-input": {
                    color: "var(--Main_icons_color)",
                  },
                }}
              />
            </Box>
            <Box
              className={`promo-filtration-filter ${dropdownOpen && "open"}`}
            >
              <PromoFilter
                setPromoCodesData={setPromoCodesData}
                searchValue={searchValue}
                toggleDropdown={handleDropdownToggle}
                dropdownOpen={dropdownOpen}
              />
            </Box>
          </Box>
          <Box className="add-new-promo" onClick={handleGoToCreatePromo}>
            <Button
              className="create-promo-btn"
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ width: "100%", textTransform: "inherit" }}
            >
              New promo code
            </Button>
          </Box>
        </Box>
        <Box className="dashboard-details">
          {isPending ? (
            <Box className="dashboard-loading">
              <CircularProgress color="inherit" />
            </Box>
          ) : (
            <Box
              className="dashboard-table"
              sx={{
                bgcolor: "transparent",
                height: "100vh",
                overflow: "auto !important",
                boxShadow: "none",
              }}
            >
              <TableContainer
                component={Paper}
                sx={{
                  bgcolor: "transparent",
                  height: "100vh",
                  overflow: "auto !important",
                  boxShadow: "none",
                  marginBottom: "10rem",
                }}
              >
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell className="column-title">Code</TableCell>
                      <TableCell align="left" className="column-title">
                        Promotion Type
                      </TableCell>
                      <TableCell align="left" className="column-title">
                        Status
                      </TableCell>
                      <TableCell align="left" className="column-title">
                        Created
                      </TableCell>
                      <TableCell align="left" className="column-title">
                        Expired date
                      </TableCell>
                      <TableCell align="left" className="column-title">
                        Usage
                      </TableCell>
                      <TableCell align="left" className="column-title">
                        Created By
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows &&
                      rows.map((row) => (
                        <TableRow
                          key={row.code}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell
                            component="th"
                            scope="row"
                            className="code-name cell-item c-pointer"
                            onClick={() => handleViewPromoCode(row.codeId)}
                          >
                            {row.code}
                          </TableCell>
                          <TableCell align="left" className="cell-item">
                            {PromotionDropdownItems[row.PromotionType]}
                          </TableCell>
                          <TableCell
                            align="left"
                            className={`cell-item cell-status-item ${PromoCodeStatus[
                              row.Status
                            ].toLowerCase()}`}
                          >
                            {PromoCodeStatus[row.Status]}
                          </TableCell>
                          <TableCell align="left" className="cell-item">
                            {formatDate(row.Created)}
                          </TableCell>
                          <TableCell align="left" className="cell-item">
                            {row.Expireddate
                              ? formatDate(row.Expireddate)
                              : "----------------"}
                          </TableCell>
                          <TableCell align="left" className="cell-item">
                            {row.Usage}
                          </TableCell>
                          <TableCell align="left" className="cell-item">
                            <Box className="createdBy-box">
                              <Typography>{row.CreatedBy}</Typography>
                              <Box className="createdBy-box-actions">
                                <IconButton
                                  onClick={() =>
                                    handleShowAlertDeletePromoCode(row.codeId)
                                  }
                                >
                                  <DeletePromoCodeIcon />
                                </IconButton>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </Box>
      {promoAlert.show && (
        <PromoCodeAlert
          promoAlert={promoAlert}
          setPromoAlert={setPromoAlert}
          deletePromoCodeRequest={handleDeletePromoCode}
        />
      )}
    </Box>
  );
};
